import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { LayoutDashboard, Home, Newspaper, Mail, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet';

export const AdminLayout = ({ children }) => {
  const { logout } = useAuth();
  const { t, switchLanguage, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  // Force French in admin panel
  useEffect(() => {
    if (language !== 'fr') {
      switchLanguage('fr');
    }
  }, [language, switchLanguage]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', label: t.admin.dashboard, icon: LayoutDashboard },
    { path: '/admin/listings', label: t.admin.manageListings, icon: Home },
    { path: '/admin/news', label: t.admin.manageNews, icon: Newspaper },
    { path: '/admin/contacts', label: t.admin.contacts, icon: Mail },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="flex h-screen bg-slate-50">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-slate-200 flex flex-col">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <img src="/logo.jpg" alt="AILE" className="h-10 w-auto" />
              <span className="font-bold text-lg text-[#127abb]">AILE Admin</span>
            </div>
          </div>

          <nav className="flex-1 p-4">
            <ul className="space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      data-testid={`admin-nav-${item.path.split('/').pop()}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive(item.path)
                          ? 'bg-primary-50 text-[#127abb] font-medium'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <Icon size={20} />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="p-4 border-t border-slate-200">
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="w-full justify-start gap-3 text-[#8c1616] border-[#8c1616] hover:bg-secondary-50"
            >
              <LogOut size={20} />
              {t.admin.logout}
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <div className="p-8">{children}</div>
        </main>
      </div>
    </>
  );
};